import React, { useEffect, useRef } from "react";
import { Animated, Easing, StyleSheet, Text, View } from "react-native";
import { Svg, Circle } from "react-native-svg";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const RING_SIZE = 18;
const RING_STROKE = 2.5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

type Props = {
  currentIndex: number;
  total: number;
  durationMs: number;
};

export const PageProgressChip = ({
  currentIndex,
  total,
  durationMs,
}: Props) => {
  const theme = useTheme();
  const styles = makeStyles(theme);
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const elapsed = Date.now() % durationMs;
    const initialProgress = elapsed / durationMs;
    const remaining = durationMs - elapsed;

    progress.setValue(initialProgress);
    Animated.timing(progress, {
      toValue: 1,
      duration: remaining,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start();
  }, [currentIndex, durationMs, progress]);

  const dashOffset = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [RING_CIRCUMFERENCE, 0],
  });

  return (
    <View style={styles.chip}>
      <Text style={styles.text}>
        <Text style={styles.current}>{currentIndex + 1}</Text>
        <Text style={styles.of}> of </Text>
        <Text style={styles.current}>{total}</Text>
      </Text>
      <Svg width={RING_SIZE} height={RING_SIZE} style={styles.ring}>
        <Circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          stroke={theme.toRgba(theme.backgroundText, 0.25)}
          strokeWidth={RING_STROKE}
          fill="none"
        />
        <AnimatedCircle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          stroke={theme.backgroundText}
          strokeWidth={RING_STROKE}
          fill="none"
          strokeDasharray={`${RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
        />
      </Svg>
    </View>
  );
};

const makeStyles = (theme: ThemeOptions) =>
  StyleSheet.create({
    chip: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: theme.spacing.small,
      paddingVertical: theme.spacing.xxsmall,
      borderRadius: theme.borderRadius.large,
      backgroundColor: theme.toRgba(theme.contrastColor, 0.15),
    },
    text: {
      fontSize: theme.fontSize.body,
      color: theme.backgroundText,
      marginRight: theme.spacing.xsmall,
    },
    current: { fontWeight: "500" },
    of: { opacity: 0.6 },
    ring: { marginLeft: theme.spacing.xxsmall },
  });
